import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { KEYBOARD_OPEN_CLASS, isTextEntry } from "./softKeyboard";

const sanitizeFilename = (name) =>
  // eslint-disable-next-line no-control-regex
  String(name).replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "_").trim() || "export";

// Android's keyboard takes about 300 ms to close.
const KEYBOARD_CLOSE_MS = 350;
const KEYBOARD_CLOSE_MAX_MS = 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The share sheet pauses the app. If the keyboard (say, from the filename prompt) is
// still closing then, Android 16 keeps its Back handler, and the next Back press is
// spent hiding a keyboard that is already gone. So the field gives up focus, and this
// resolves once the keyboard is down: the viewport has grown back and the close
// animation has had its time, or after a second at most.
const keyboardClosed = (win = window) => {
  const doc = win.document;
  const open = doc.body.classList.contains(KEYBOARD_OPEN_CLASS);
  if (isTextEntry(doc.activeElement)) doc.activeElement.blur();
  if (!open) return Promise.resolve();

  const target = win.visualViewport || win;
  const height = () => (win.visualViewport ? win.visualViewport.height : win.innerHeight);
  const before = height();
  let onResize;
  const grownBack = new Promise((resolve) => {
    onResize = () => height() > before && resolve();
    target.addEventListener("resize", onResize);
  });
  return Promise.race([
    Promise.all([grownBack, sleep(KEYBOARD_CLOSE_MS)]),
    sleep(KEYBOARD_CLOSE_MAX_MS),
  ]).finally(() => target.removeEventListener("resize", onResize));
};

// Saves a text file for the user. Browsers download it. The native apps' WebViews can't
// (Android has no DownloadListener, iOS no WKDownload handling), so there the file is
// written to the cache and handed to the system share sheet (Files, Drive, Mail, …).
// Resolves once done; closing the share sheet without picking a target isn't an error.
export async function saveTextFile(filename, text, mimeType) {
  const name = sanitizeFilename(filename);

  if (!Capacitor.isNativePlatform()) {
    const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return;
  }

  const [{ uri }] = await Promise.all([
    Filesystem.writeFile({
      path: name,
      data: text,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    }),
    keyboardClosed(),
  ]);
  try {
    await Share.share({ title: name, files: [uri] });
  } catch (err) {
    if (!/cancel/i.test(err?.message || "")) throw err;
  }
}
